type KeyValueStorage = Pick<Storage, "getItem" | "setItem">;

const PRODUCT_USED_KEY = "product-used";
const AFTER_FIRST_USE_MODAL_KEY = "after-first-use-membership-modal";

const browserStorage = (): KeyValueStorage | null =>
	typeof window === "undefined" ? null : window.localStorage;

const read = (
	prefix: string,
	userId: string | null | undefined,
	storage: KeyValueStorage | null,
): boolean => {
	if (!userId || !storage) return false;

	try {
		return storage.getItem(`${prefix}:${userId}`) === "true";
	} catch {
		return false;
	}
};

const write = (
	prefix: string,
	userId: string | null | undefined,
	storage: KeyValueStorage | null,
): void => {
	if (!userId || !storage) return;

	try {
		storage.setItem(`${prefix}:${userId}`, "true");
	} catch {
		// Private browsing or a full quota: the experiment arm just waits longer.
	}
};

/**
 * Whether this account has taken a real product action on this browser
 * (finished a course section, an exam, or asked the AI tutor something).
 *
 * @param userId - Signed-in account id
 * @param storage - Defaults to `localStorage`
 * @returns True once `markProductUsed` ran for this account
 */
export const hasUsedProduct = (
	userId: string | null | undefined,
	storage: KeyValueStorage | null = browserStorage(),
): boolean => read(PRODUCT_USED_KEY, userId, storage);

/**
 * Records a real product action for this account.
 *
 * @param userId - Signed-in account id
 * @param storage - Defaults to `localStorage`
 * @returns {void}
 */
export const markProductUsed = (
	userId: string | null | undefined,
	storage: KeyValueStorage | null = browserStorage(),
): void => write(PRODUCT_USED_KEY, userId, storage);

/**
 * Whether the after-first-use membership pop-up already opened for this account.
 *
 * @param userId - Signed-in account id
 * @param storage - Defaults to `localStorage`
 * @returns True once `markAfterFirstUseModalShown` ran for this account
 */
export const hasShownAfterFirstUseModal = (
	userId: string | null | undefined,
	storage: KeyValueStorage | null = browserStorage(),
): boolean => read(AFTER_FIRST_USE_MODAL_KEY, userId, storage);

/**
 * Records that the after-first-use membership pop-up opened for this account.
 *
 * @param userId - Signed-in account id
 * @param storage - Defaults to `localStorage`
 * @returns {void}
 */
export const markAfterFirstUseModalShown = (
	userId: string | null | undefined,
	storage: KeyValueStorage | null = browserStorage(),
): void => write(AFTER_FIRST_USE_MODAL_KEY, userId, storage);
